import {
  AttachmentFolderInstanceDetailDto,
  AttachmentFolderInstanceMergeDto,
  AttachmentFolderInstanceSubFolderDto,
  BuildingAttachmentFolderInstanceDto,
  BuildingAttachmentFolderInstanceMergeDto,
  BuildingDetailDto,
  BuildingEquipmentAttachmentFolderInstanceDto,
  BuildingEquipmentAttachmentFolderInstanceMergeDto,
  BuildingEquipmentDetailDto,
} from '@artemis-software/wr-api';
import { AttachmentFolderInstance, BuildingDocumentsValue } from './attachment-folder-tree.component';
import { attachmentFromDetail, attachmentToMerge } from '@shared/form-controls/attachment-upload/attachment-utils';

function subFolderDtoToFolderInstance(dto: AttachmentFolderInstanceSubFolderDto): AttachmentFolderInstance {
  return {
    id: dto.id,
    attachmentFolder: dto.attachmentFolder,
    name: dto.name,
    color: dto.color,
    targetId: dto.targetId,
    parentId: dto.parentId,
    attachments: dto.attachments?.map((a) => attachmentFromDetail(a)) || undefined,
    publicFolder: dto.publicFolder,
  };
}

export function folderInstanceDtosToFolderInstances(
  instances?: AttachmentFolderInstanceDetailDto[],
): AttachmentFolderInstance[] {
  if (!instances) {
    return [];
  }
  return instances.map((folderInstance: AttachmentFolderInstanceDetailDto) => {
    return {
      id: folderInstance.id,
      attachmentFolder: folderInstance.attachmentFolder,
      name: folderInstance.name,
      color: folderInstance.color,
      targetId: folderInstance.targetId,
      parentId: folderInstance.parentId,
      attachments: folderInstance.attachments?.map((a) => attachmentFromDetail(a)) || undefined,
      subFolders: folderInstance.subFolders?.map((sub) => subFolderDtoToFolderInstance(sub)) ?? [],
      publicFolder: folderInstance.publicFolder,
    };
  });
}

export function folderInstancesToMergeDtos(
  instances: AttachmentFolderInstance[],
): AttachmentFolderInstanceMergeDto[] {
  return instances
    .flatMap((folderInstance) => [folderInstance, ...(folderInstance.subFolders ?? [])])
    .map((folderInstance: AttachmentFolderInstance) => {
      return {
        id: folderInstance.id,
        attachmentFolderId: undefined,
        targetId: undefined,
        parentId: undefined,
        name: undefined,
        color: undefined,
        attachments: folderInstance.attachments?.map(a => attachmentToMerge(a)) || undefined,
      };
    });
}

export function appendSubFolderToValue(
  value: BuildingDocumentsValue,
  parentId: string,
  subFolder: AttachmentFolderInstance,
): BuildingDocumentsValue {
  return {
    ...value,
    folderInstances: value.folderInstances.map((root) =>
      root.id === parentId
        ? { ...root, subFolders: [...(root.subFolders ?? []), subFolder] }
        : root),
  };
}

export function renameSubFolderInValue(
  value: BuildingDocumentsValue,
  subFolder: AttachmentFolderInstance,
  name: string,
  color: string,
): BuildingDocumentsValue {
  return {
    ...value,
    folderInstances: value.folderInstances.map((root) =>
      root.id === subFolder.parentId
        ? {
          ...root,
          subFolders: root.subFolders?.map((sub) =>
            sub.id === subFolder.id ? { ...sub, name, color } : sub),
        }
        : root),
  };
}

export function removeSubFolderFromValue(
  value: BuildingDocumentsValue,
  subFolder: AttachmentFolderInstance,
): BuildingDocumentsValue {
  return {
    ...value,
    folderInstances: value.folderInstances.map((root) =>
      root.id === subFolder.parentId
        ? { ...root, subFolders: root.subFolders?.filter((sub) => sub.id !== subFolder.id) }
        : root),
  };
}

export function buildingDocumentsDtoToValue(
  dto: BuildingAttachmentFolderInstanceDto | BuildingDetailDto,
): BuildingDocumentsValue {
  return {
    folderInstances: folderInstanceDtosToFolderInstances(dto.attachmentFolderInstances),
    rootAttachments: dto.rootAttachments?.map((a) => attachmentFromDetail(a)),
  };
}

export function buildingDocumentsValueToMergeDto(
  buildingId: string,
  value: BuildingDocumentsValue,
): BuildingAttachmentFolderInstanceMergeDto {
  return {
    id: buildingId,
    attachmentFolderInstances: folderInstancesToMergeDtos(value.folderInstances),
    rootAttachments: value.rootAttachments?.map((a) => attachmentToMerge(a)),
  };
}

export function buildingEquipmentDocumentsDtoToValue(
  dto: BuildingEquipmentAttachmentFolderInstanceDto | BuildingEquipmentDetailDto,
): BuildingDocumentsValue {
  return {
    folderInstances: folderInstanceDtosToFolderInstances(dto.attachmentFolderInstances ?? undefined),
    rootAttachments: dto.rootAttachments?.map((a) => attachmentFromDetail(a)),
  };
}

export function buildingEquipmentDocumentsValueToMergeDto(
  buildingEquipmentId: string,
  value: BuildingDocumentsValue,
): BuildingEquipmentAttachmentFolderInstanceMergeDto {
  return {
    id: buildingEquipmentId,
    attachmentFolderInstances: folderInstancesToMergeDtos(value.folderInstances),
    rootAttachments: value.rootAttachments?.map((a) => attachmentToMerge(a)),
  };
}
